import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Store, Tag, Map as MapIcon, ArrowRight, Globe } from "lucide-react";
import api from "../api";
import DealCard from "../components/dashboard/DealCard";
import { InviteBusiness } from "../components/InviteBusiness";
import { useLang } from "../context/LanguageContext";
import { useSeo } from "../hooks/useSeo";
import { catLabel } from "../utils/categoryLabel";

const LANGS = ["es", "fr", "pt", "de"];

const CityDeals = () => {
  const { lang: urlLang, countrySlug, citySlug } = useParams();
  const { t, lang, setLang } = useLang();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (urlLang && LANGS.includes(urlLang) && urlLang !== lang) setLang(urlLang);
  }, [urlLang]); // eslint-disable-line

  useEffect(() => {
    setState("loading");
    api.get(`/cities/${countrySlug}/${citySlug}`, { params: { lang: urlLang || lang } })
      .then((r) => { setData(r.data); setState("ready"); })
      .catch(() => setState("missing"));
  }, [countrySlug, citySlug]);

  const canonical = `/${countrySlug}/${citySlug}`;
  const dealsTxt = data && (data.dealCount === 1 ? t("city.liveDealOne") : t("city.liveDeals", { n: data.dealCount }));
  const shopsTxt = data && (data.shopCount === 1 ? t("city.shopOne") : t("city.shops", { n: data.shopCount }));
  useSeo(data ? {
    title: t("city.seoTitle", { city: data.city, country: data.country }),
    description: data.dealCount
      ? t("city.seoDesc", { deals: dealsTxt, shops: shopsTxt, city: data.city, country: data.country })
      : t("city.seoDescEmpty", { city: data.city, country: data.country }),
    image: (data.deals[0] || {}).image,
    noindex: !data.indexable,
    canonical,
  } : {});

  if (state === "loading") return <div className="h-96 rounded-2xl bg-secondary animate-pulse" />;
  if (state === "missing") {
    return (
      <div className="text-center py-20" data-testid="city-not-found">
        <p className="text-muted-foreground mb-4">{t("city.noPage")}</p>
        <Link to="/cities" className="text-[#16a34a] font-semibold">{t("city.allCities")}</Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 lp-fade-up" data-testid="city-page">
      <header className="space-y-3 max-w-3xl">
        <Link to="/cities" className="inline-flex items-center gap-1.5 text-xs font-bold text-[#16a34a] hover:underline" data-testid="city-breadcrumb">
          <MapPin size={13} /> {t("city.allCities")}
        </Link>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-foreground" data-testid="city-title">
          {t("city.heading", { city: data.city })}
        </h1>
        <p className="text-base md:text-lg text-muted-foreground" data-testid="city-subline">
          {data.dealCount
            ? t("city.subline", { deals: dealsTxt, shops: shopsTxt, city: data.city, country: data.country })
            : t("city.sublineEmpty", { city: data.city, country: data.country })}
        </p>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-foreground" data-testid="city-deal-count">
            <Tag size={15} className="text-[#16a34a]" /> {dealsTxt}
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-foreground" data-testid="city-shop-count">
            <Store size={15} className="text-[#16a34a]" /> {shopsTxt}
          </span>
          <Link to="/lytemap" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#16a34a] hover:underline" data-testid="city-map-link">
            <MapIcon size={15} /> {t("city.onTheMap")}
          </Link>
        </div>
      </header>

      {data.topCategories.length > 0 && (
        <nav className="flex flex-wrap gap-2" data-testid="city-categories">
          {data.topCategories.map((c) => (
            <Link key={c.name} to={`/browse?category=${encodeURIComponent(c.name)}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-semibold text-foreground hover:border-[#16a34a] hover:text-[#16a34a] transition-colors"
              data-testid={`city-category-${c.name.toLowerCase()}`}>
              {catLabel(c.name, t)} <span className="text-muted-foreground font-medium">{c.n}</span>
            </Link>
          ))}
        </nav>
      )}

      {data.deals.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="city-deal-grid">
          {data.deals.map((d) => (<DealCard key={d.id} deal={d} />))}
        </div>
      ) : (
        <div className="rounded-3xl border border-dashed border-border bg-card p-8 text-center space-y-3" data-testid="city-empty">
          <p className="font-bold text-foreground">{t("city.emptyTitle", { city: data.city })}</p>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">{t("city.emptyBody", { city: data.city })}</p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-1">
            <InviteBusiness city={data.city} country={data.country} variant="button" />
            <Link to="/browse" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#16a34a] hover:underline" data-testid="city-empty-browse">
              {t("city.browseEverywhere")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}

      <section className="rounded-3xl border border-border bg-secondary/40 p-6 space-y-2 max-w-3xl" data-testid="city-about">
        <h2 className="text-base md:text-lg font-bold text-foreground">{t("city.aboutTitle", { city: data.city })}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t("city.aboutBody", {
            city: data.city, country: data.country,
            cats: data.topCategories.map((c) => catLabel(c.name, t)).join(", ") || t("city.everyCategory"),
          })}
        </p>
        {data.copy?.text && (
          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="city-copy">{data.copy.text}</p>
        )}
        <p className="text-sm text-muted-foreground leading-relaxed">{t("city.aboutTrust")}</p>
      </section>

      {data.nearby.length > 0 && (
        <section className="space-y-3" data-testid="city-nearby">
          <h2 className="text-base md:text-lg font-bold text-foreground">{t("city.nearbyTitle")}</h2>
          <div className="flex flex-wrap gap-2">
            {data.nearby.map((n) => (
              <Link key={`${n.countrySlug}-${n.citySlug}`} to={`/${n.countrySlug}/${n.citySlug}`}
                data-testid={`city-nearby-${n.citySlug}`}
                className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground hover:border-[#16a34a] hover:text-[#16a34a] transition-colors">
                <MapPin size={14} className="text-[#16a34a]" /> {n.city}
                <span className="text-muted-foreground font-medium">{n.dealCount}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <nav className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="city-lang-links">
        <span className="inline-flex items-center gap-1.5 font-semibold"><Globe size={13} /> {t("city.otherLanguages")}</span>
        <Link to={canonical} className="hover:text-[#16a34a]" hrefLang="en">English</Link>
        {(data.langs || LANGS).map((l) => (
          <Link key={l} to={`/${l}${canonical}`} className="hover:text-[#16a34a]" hrefLang={l}>{l.toUpperCase()}</Link>
        ))}
      </nav>
    </div>
  );
};

export default CityDeals;
